import dayjs from 'dayjs';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { checkFutureDate, checkValidDate, isValidDate, isValidName, isValidZipCode } from '../../../Utils/validation';

export const validationSchema = (addSecondaryInsurance: boolean): any => {
  return Yup.object().shape({
    insurance_through: Yup.string().required('This field is required'),
    insurance_carrier_id: Yup.number().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.number()
        .transform((value, originalValue) => (originalValue === null ? undefined : value))
        .required('Insurance carrier is required'),
      otherwise: Yup.number(),
    }),
    insurance_phone_number: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    member_id: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Member ID is required')
        .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    group_number: Yup.string().nullable(),
    insurance_address: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance Address is required')
        .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_city: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance City is required')
        .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_state: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance State is required')
        .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_zip_code: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined,
        then: Yup.string().required('Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    insurance_first_name: Yup.string().when(['insurance_through'], {
      is: (val: string) => {
        return val !== 'none' && val !== undefined && val !== 'myself';
      },
      then: Yup.string().required('Insurance Holder First Name is required'),
    }),
    insurance_last_name: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Last Name is required'),
    }),
    insurance_date_of_birth: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
        if (value && !isValidDate(formattedDate)) {
          return false;
        }
        if (value) {
          const values = formattedDate?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const formattedDate = dayjs(value).format('MM/DD/YYYY');
          const values = formattedDate?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    insurance_gender: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string()
        .required('Insurance Holder Gender is required')
        .test('test-name', 'Enter Valid Gender', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_address1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Address is required'),
    }),
    insurance_city1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder city Required'),
    }),
    insurance_state1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder state Required'),
    }),
    insurance_zip_code1: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    state_funded_coverage_status: Yup.string().when('insurance_through', {
      is: (val: string) => val && val.trim() !== '' && val !== 'none',
      then: Yup.string().required(
        'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
      ),
      otherwise: Yup.string(),
    }),
    secondary_insurance_through: Yup.string().test(
      'secondary_insurance_through',
      'Secondary Insurance is required',
      (value) => {
        return addSecondaryInsurance ? value?.trim() !== '' : true;
      },
    ),
    secondary_insurance_carrier_id: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value;
      })
      .when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val !== undefined,
        then: (schema) =>
          schema.test('secondary_carrier_required', 'Secondary Insurance carrier ID is required', (value) => {
            return addSecondaryInsurance ? value !== undefined && value !== null : true;
          }),
        otherwise: (schema) => schema,
      }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_insurance_phone_number: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    secondary_insurance_first_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder First Name is required')
        .test('test-name', 'Enter Valid First Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_last_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Last Name is required')
        .test('test-name', 'Enter Valid Last Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_date_of_birth: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
        if (value && !isValidDate(formattedDate)) {
          return false;
        }
        if (value) {
          const values = formattedDate?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Secondary Date of Birth can not be in the future.', (value) => {
        if (value) {
          const formattedDate = dayjs(value).format('MM/DD/YYYY');
          const values = formattedDate?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    secondary_insurance_gender: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string()
        .required('Secondary Insurance Holder Gender is required')
        .test('test-name', 'Enter Valid Gender', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_address1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder Address is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder city Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder state Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder ZipCode is required')
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
  });
};

export type InsuranceTab = 'primary_insurance' | 'secondary_insurance';

export const validationSchemaNewDesign = (currentTab: InsuranceTab): any => {
  const isPrimaryTab = currentTab === 'primary_insurance';
  const isSecondaryTab = currentTab === 'secondary_insurance';
  if (isPrimaryTab) {
    return Yup.object().shape({
      /* ===================== PRIMARY INSURANCE ===================== */
      insurance_carrier_id: Yup.number().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.number()
          .transform((value, originalValue) => (originalValue === null ? undefined : value))
          .required('Insurance carrier is required'),
        otherwise: Yup.number(),
      }),
      member_id: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.string()
          .required('Member ID is required')
          .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
            if (value) {
              return !/^\s+$/.test(value);
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      group_number: Yup.string().nullable(),
      insurance_phone_number: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.string()
          .required('Insurance phone number is required')
          .test('test-name', 'Enter Valid Phone Number', (value) => {
            if (value && !isValidPhoneNumber(value)) {
              return false;
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_address: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.string()
          .required('Insurance Address is required')
          .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
            if (value) {
              return !/^\s+$/.test(value);
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_city: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.string()
          .required('Insurance City is required')
          .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
            if (value) {
              return !/^\s+$/.test(value);
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_state: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '',
        then: Yup.string()
          .required('Insurance State is required')
          .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
            if (value) {
              return !/^\s+$/.test(value);
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_zip_code: Yup.string()
        .when(['insurance_through'], {
          is: (val: string) => val !== 'none' && val !== '',
          then: Yup.string().required('Insurance ZipCode is required'),
        })
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      insurance_through: Yup.string()
        .required('This field is required')
        .notOneOf(['someone_else'], 'Please select who the policy holder is'),
      insurance_first_name: Yup.string().when(['insurance_through'], {
        is: (val: string) => {
          return val !== 'none' && val !== '' && val !== 'myself';
        },
        then: Yup.string().required('Insurance Holder First Name is required'),
      }),
      insurance_last_name: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder Last Name is required'),
      }),
      insurance_date_of_birth: Yup.string()
        .when(['insurance_through'], {
          is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
          then: Yup.string().required('Insurance Holder Date of birth is required'),
        })
        .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
          const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
          if (value && !isValidDate(formattedDate)) {
            return false;
          }
          if (value) {
            const values = formattedDate?.split('/');
            if (!checkValidDate(values[0], values[1], values[2])) {
              return false;
            }
          }
          return true;
        })
        .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
          if (value) {
            const formattedDate = dayjs(value).format('MM/DD/YYYY');
            const values = formattedDate?.split('/');
            if (!checkFutureDate(values[0], values[1], values[2])) {
              return false;
            }
          }
          return true;
        }),
      insurance_gender: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
        then: Yup.string()
          .required('Insurance Holder Gender is required')
          .test('test-name', 'Enter Valid Gender', (value) => {
            if (value && !isValidName(value)) {
              return false;
            }
            return true;
          }),
        otherwise: Yup.string(),
      }),
      insurance_address1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder Address is required'),
      }),
      insurance_city1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder city Required'),
      }),
      insurance_state1: Yup.string().when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder state Required'),
      }),
      insurance_zip_code1: Yup.string()
        .when(['insurance_through'], {
          is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
          then: Yup.string().required('Insurance Holder ZipCode is required'),
        })
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      state_funded_coverage_status: Yup.string().when('insurance_through', {
        is: (val: string) => val && val.trim() !== '' && val !== 'none',
        then: Yup.string().required(
          'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
        ),
        otherwise: Yup.string(),
      }),
    });
  }
  /* ===================== SECONDARY INSURANCE ===================== */
  return Yup.object().shape({
    secondary_insurance_carrier_id: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value;
      })
      .when('secondary_insurance_through', {
        is: (val: string) => val !== 'none' && val !== '',
        then: (schema) =>
          schema.test('secondary_carrier_required', 'Secondary Insurance carrier ID is required', (value) => {
            return isSecondaryTab ? value !== undefined && value !== null : true;
          }),
        otherwise: (schema) => schema,
      }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_insurance_phone_number: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
      then: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && isSecondaryTab,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    secondary_insurance_through: Yup.string().test(
      'required-secondary',
      'Secondary Insurance is required',
      (value) => !!value && value.trim() !== '' && value !== 'someone_else',
    ),
    secondary_insurance_first_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string()
        .required('Secondary Insurance Holder First Name is required')
        .test('test-name', 'Enter Valid First Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_last_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string()
        .required('Secondary Insurance Holder Last Name is required')
        .test('test-name', 'Enter Valid Last Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_date_of_birth: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
        then: Yup.string().required('Secondary Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
        if (value && !isValidDate(formattedDate)) {
          return false;
        }
        if (value) {
          const values = formattedDate?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Secondary Date of Birth can not be in the future.', (value) => {
        if (value) {
          const formattedDate = dayjs(value).format('MM/DD/YYYY');
          const values = formattedDate?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    secondary_insurance_gender: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself',
      then: Yup.string()
        .required('Secondary Insurance Holder Gender is required')
        .test('test-name', 'Enter Valid Gender', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_address1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string().required('Secondary Insurance Holder Address is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string().required('Secondary Insurance Holder city Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string().required('Secondary Insurance Holder state Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== '' && val !== 'myself' && isSecondaryTab,
      then: Yup.string()
        .required('Secondary Insurance Holder ZipCode is required')
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
  });
};
